// src/components/results/ResultsTreatSelection.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";

import { getTreatProducts, type TreatProduct } from "@/helpers/getTreatProducts";
import { filterRecipesByAllergies } from "@/helpers/mealDistribution";
import TreatCard from "../shared/TreatCard";
import { useTreats } from "@/state/useTreats";
import { GTM } from "@/services/gtm";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  dogs: { id: string; form: any }[];
};

/**
 * Treats are selected *per dog*, same pattern as meals.
 * We simply map each dog → its own treat selection container.
 */
export default function ResultsTreatSelection({ dogs }: Props) {
  const [allTreats, setAllTreats] = useState<TreatProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* Load treat products */
  useEffect(() => {
    let mounted = true;
    async function load() {
      const result = await getTreatProducts();
      if (mounted) {
        setAllTreats(result.products);
        setError(result.error ?? null);
        setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return <Typography>Loading treats...</Typography>;

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography sx={{ color: "#D14C3F", fontWeight: 600 }}>
          {error}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "#666", mt: 1 }}>
          Please refresh the page to try again.
        </Typography>
      </Box>
    );
  }

  if (!allTreats.length) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography sx={{ color: "#4A3428" }}>
          No treats available at this time.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 4 },
        py: { xs: 2, md: 4 },
      }}
    >
      {/* Headings (Figma 15) */}
      <Box sx={{ textAlign: "center", mb: { xs: "26px", md: "36px" } }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: QUIZ.font.display,
            fontWeight: 900,
            textTransform: "uppercase",
            color: QUIZ.color.ink,
            fontSize: { xs: "34px", md: "41px" },
            lineHeight: 1.1,
          }}
        >
          Treats
        </Typography>
        <Typography
          sx={{
            fontFamily: QUIZ.font.body,
            fontSize: { xs: 16, md: 17 },
            color: QUIZ.color.ink,
            opacity: 0.9,
            mt: "8px",
            maxWidth: { xs: 300, md: "none" },
            mx: "auto",
          }}
        >
          Treats as good on the inside as they are taste-wise.
        </Typography>
        <Typography
          sx={{
            fontFamily: QUIZ.font.label,
            fontSize: { xs: 21, md: 24 },
            color: QUIZ.color.ink,
            mt: { xs: "18px", md: "24px" },
          }}
        >
          Add extras to your plan
        </Typography>
      </Box>

      {dogs.map((dog) => (
        <DogTreatSelection
          key={dog.id}
          dog={dog}
          treats={allTreats}
        />
      ))}
    </Box>
  );
}

/* -------------------------------------------------------
 * Per-dog Wrapper Component
 * ------------------------------------------------------ */

function DogTreatSelection({
  dog,
  treats: allTreats,
}: {
  dog: { id: string; form: any };
  treats: TreatProduct[];
}) {
  const { selections, setTreats } = useTreats(dog.id);

  // Per-dog allergy filtering, same rules as meal selection (title keywords,
  // synonyms like fish→salmon, hidden allergens like Sammy's & Tilly's→chicken).
  const treats = useMemo(
    () => filterRecipesByAllergies(allTreats, (dog.form?.allergies as string[] | undefined) ?? []),
    [allTreats, dog.form?.allergies]
  );

  const increment = (variantId: string, treat: TreatProduct) => {
    const newQuantity = (selections?.[variantId] ?? 0) + 1;
    setTreats((prev: any = {}) => ({
      ...prev,
      [variantId]: newQuantity,
    }));

    // Track treat selection
    GTM.trackTreatSelected({
      variantId,
      treatName: treat.title,
      quantity: newQuantity,
      price: parseFloat(treat.price) || undefined,
    });
  };

  const decrement = (variantId: string) => {
    setTreats((prev: any = {}) => {
      const current = prev?.[variantId] ?? 0;
      if (current <= 0) return prev;
      return { ...prev, [variantId]: current - 1 };
    });
  };

  // Total selected treats for this dog
  const totalSelectedTreats = useMemo(
    () =>
      Object.values(selections || {}).reduce(
        (sum, n) => sum + Number(n || 0),
        0
      ),
    [selections]
  );

  const dogName = dog.form?.name || "Your dog";

  return (
    <Box
      sx={{
        mb: { xs: 3, md: 5 },
      }}
    >
      {/* Dog header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: { xs: 1.5, md: 2.5 }, // ✅ tighter
          gap: 1.25,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontFamily: QUIZ.font.display,
            fontWeight: 900,
            textTransform: "uppercase",
            fontSize: { xs: "1.25rem", md: "1.6rem" },
            color: QUIZ.color.ink,
            lineHeight: 1.1,
            minWidth: 0,
            wordBreak: "break-word",
          }}
        >
          {dogName}'s Treats
        </Typography>

        <Chip
          label={`Selected: ${totalSelectedTreats}`}
          size="small"
          sx={{
            background: QUIZ.color.cream2,
            color: QUIZ.color.brown,
            fontFamily: QUIZ.font.body,
            fontWeight: 700,
            borderRadius: "999px",
            height: 28,
            "& .MuiChip-label": { px: 1.25, fontSize: "0.82rem" },
          }}
        />
      </Box>

      {treats.length === 0 && (
        <Typography sx={{ fontFamily: QUIZ.font.body, color: QUIZ.color.ink, opacity: 0.8 }}>
          No suitable treats for {dogName} based on their allergies.
        </Typography>
      )}

      {/* Treat grid — stacked wide cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: { xs: 1.5, md: 2 },
          alignItems: "stretch",
        }}
      >
        {treats.map((treat) => (
          <Box key={treat.variantId} sx={{ minWidth: 0 }}>
            <TreatCard
              recipe={treat as any}
              count={selections?.[String(treat.variantId)] ?? 0}
              onIncrement={() => increment(String(treat.variantId), treat as any)}
              onDecrement={() => decrement(String(treat.variantId))}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}